/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : LegalTables.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/* global globalThis */
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import CommonBreadcrumbs from "../../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";
import AddIcon from "@mui/icons-material/Add";
import CustomSelectV2 from "../../../components/customComponentsV2/common/select/CustomSelectV2";
import { useDispatch } from "react-redux";
import { HandleApiActions, legalRequirements } from "../../../redux/actions";
import AddButtonV2 from "../../../components/customComponentsV2/common/button/AddButtonV2";

import { CustomFilesUploadButton } from "../../../components";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import testCasesActions from "../../../redux/actions/testCasesActions";
import ViewImageDialog from "../ViewImageDialog";
import { toast } from "react-toastify";
import TableDialog from "../TableDialog";
import RowColumnDialog from "../RowColumnDialog";
import InfoButton from "../../../components/infoButton/InfoButton";

function LegalTables() {
  const dispatch = useDispatch();
  const [selectedStandard, setSelectedStandard] = useState(null);
  const [standardOptionsList, setStandardOptionsList] = useState([]);

  const [lsTableOptionsList, setLsTableOptionsList] = useState([]);
  const [selectedLSTable, setSelectedLSTable] = useState(null);
  const [selectedLSTableDetails, setSelectedLSTableDetails] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);

  const [openDialog, setOpenDialog] = useState(false);
  const [editedCells, setEditedCells] = useState({});

  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [openDialogTable, setOpenDialogTable] = useState(false);
  const [openRowColumn, setOpenRowColumn] = useState(false);

  useEffect(() => {
    if (!selectedLSTableDetails) return;

    setColumns(selectedLSTableDetails.legal_table_columns || []);
    setRows(selectedLSTableDetails.legal_table_rows || []);

    const cellMap = {};
    for (const cell of selectedLSTableDetails.legal_table_cells || []) {
      const key = `${cell?.row_id}_${cell?.column_id}`;
      cellMap[key] = cell?.content || "";
    }

    setEditedCells({});
  }, [selectedLSTableDetails]);

  const updateCell = (rowId, colId, value) => {
    const key = `${rowId}_${colId}`;

    setEditedCells((prev) => ({
      ...prev,
      [key]: {
        row_id: rowId,
        column_id: colId,
        value,
      },
    }));
  };

  const isDisableSubmit = () => {
    return Object.keys(editedCells).length === 0;
  };

  console.log(
    "standardOptionsListstandardOptionsListstandardOptionsListstandardOptionsList",
    standardOptionsList,
  );

  const handleGetLegalStandardList = useCallback(async () => {
    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...legalRequirements.get_legal_standard_main_details,
          params: {
            page_number: 1,
            page_size: 1000000,
          },
          show_toast: false,
        }),
      ),
    );
    console.log("----Legal Standard Details ----", resp);
    return resp;
  }, [dispatch]);

  const refreshLegalStandards = useCallback(async (defaultFirstSelect) => {
    const resp = await handleGetLegalStandardList();
    const rawList = resp?.data || [];

    const standardList = (
      Array.isArray(rawList) ? rawList : rawList?.data || []
    )?.map((el) => ({
      value: el.ls_id,
      label: `${el.standard_number} - ${el.version}`,
    }));

    setStandardOptionsList(standardList);

    if (standardList.length > 0 && defaultFirstSelect) {
      setSelectedStandard(standardList[0].value);
    }
  }, [handleGetLegalStandardList]);

  useEffect(() => {
    refreshLegalStandards(true);
  }, [refreshLegalStandards]);

  console.log(
    "selectedStandardselectedStandardselectedStandardselectedStandard",
    selectedStandard,
  );

  const fetchTotalTableDetails = useCallback(async () => {
    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...legalRequirements.get_legal_standard_table_details,
          params: { ls_id: selectedStandard },
        }),
      ),
    );

    console.log("resp", resp);

    if (!resp?.error && Array.isArray(resp.data)) {
      const tableOptions = resp.data
        .map((item) => ({
          label: item.legal_standard_table?.title || "",
          value: item.legal_standard_table?.table_id || null,
        }))
        .filter((opt) => opt.value !== null); // remove invalid entries

      setLsTableOptionsList(tableOptions);

      if (tableOptions.length > 0) {
        setSelectedLSTable(tableOptions[0].value);
      } else {
        setSelectedLSTable(null);
      }
    } else {
      setLsTableOptionsList([]);
      setSelectedLSTable(null);
    }
  }, [dispatch, selectedStandard]);

  useEffect(() => {
    if (!selectedStandard) {
      setSelectedLSTable(null);
      return;
    }

    fetchTotalTableDetails();
  }, [selectedStandard, fetchTotalTableDetails]);

  useEffect(() => {
    if (lsTableOptionsList.length === 0) return;
    setSelectedLSTable((prev) => prev ?? lsTableOptionsList[0].value);
  }, [lsTableOptionsList]);

  useEffect(() => {
    if (!selectedLSTable || !selectedStandard) return;

    const fetchTableDetails = async () => {
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...legalRequirements.get_legal_standard_table_details,
            params: {
              ls_id: selectedStandard,
              table_id: selectedLSTable,
            },
          }),
        ),
      );

      if (resp?.data?.length > 0) {
        setSelectedLSTableDetails(resp?.data[0]?.legal_standard_table || null);
      }
    };

    fetchTableDetails();
  }, [selectedLSTable, selectedStandard, dispatch]);

  const handleViewPhoto = async (fileName) => {
    if (!fileName) return;

    try {
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...testCasesActions.get_files,
            params: { filename: fileName },
            is_get_file: true,
          }),
        ),
      );

      if (!resp) {
        alert("Failed to load image.");
        return;
      }

      const blob = new Blob([resp], { type: "image/*" });
      const url = globalThis.URL.createObjectURL(blob);

      setImageUrl(url);
      setOpenDialog(true);
      console.log("sdnmadbafkjdgafkkkkkkkadvsj zavsh");
    } catch (error) {
      console.error("Error fetching image:", error);
      alert("Something went wrong while opening the image.");
    }
  };

  const handleFilesUpload = async (files, rowId, colId, fileType = "image") => {
    const file = files?.[0];
    if (!file?.type?.startsWith(`${fileType}/`)) {
      toast.error("Invalid file type.");
      return { error: true };
    }

    const res = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...testCasesActions.upload_files,
          show_toast: true,
          file,
          is_file: true,
        }),
      ),
    );

    if (!res?.error && res?.file_name) {
      updateCell(rowId, colId, res.file_name);
      return { error: false };
    }

    return { error: true };
  };

  const handleAddRow = async () => {
    if (!selectedLSTableDetails) return;

    const reqObj = {
      table_id: selectedLSTableDetails.table_id,
      row_order: (selectedLSTableDetails.legal_table_rows?.length || 0) + 1,
    };

    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...legalRequirements.add_legal_standard_table_row,
          params: reqObj,
        }),
      ),
    );

    if (!resp?.error) {
      const updatedResp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...legalRequirements.get_legal_standard_table_details,
            params: {
              ls_id: selectedStandard,
              table_id: selectedLSTable,
            },
          }),
        ),
      );
      if (updatedResp?.data?.length > 0) {
        setSelectedLSTableDetails(updatedResp?.data[0]?.legal_standard_table);
      }
    }
  };

  const handleSubmit = async () => {
    if (!selectedLSTableDetails) return;

    const reqObj = {
      table_id: selectedLSTableDetails.table_id,
      cells: Object.values(editedCells),
    };
    console.log(
      "editedCellseditedCellseditedCells",
      Object.values(editedCells),
    );
    console.log("reqObjreqObjreqObjreqObjreqObj", reqObj);

    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...legalRequirements.add_legal_standard_table_cells,
          params: reqObj,
          show_toast: true,
        }),
      ),
    );

    if (!resp?.error) {
      setEditedCells({});
      const updatedResp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...legalRequirements.get_legal_standard_table_details,
            params: {
              ls_id: selectedStandard,
              table_id: selectedLSTable,
            },
          }),
        ),
      );
      if (updatedResp?.data?.length > 0) {
        setSelectedLSTableDetails(updatedResp?.data[0]?.legal_standard_table);
      }
    }
  };

  console.log("selectedLSTableselectedLSTableselectedLSTable", selectedLSTable);

  const renderCellContent = (submittedCell, col, row, cellKey) => {
    if (submittedCell?.content !== undefined && submittedCell?.content !== null) {
      if (col.column_type === "string") {
        return (
          <Typography variant="body2">
            {submittedCell.content}
          </Typography>
        );
      }
      return (
        <CustomButtonV2
          btnName="View"
          size="small"
          type="other"
          btnStyle={{
            textDecoration: "underline",
            padding: 1,
            minWidth: "auto",
            fontWeight: 500,
            color: "#0766DB",
          }}
          handleOnClick={() => handleViewPhoto(submittedCell.content)}
        />
      );
    }

    if (col.column_type === "string") {
      return (
        <TextField
          size="small"
          variant="standard"
          fullWidth
          value={editedCells[cellKey]?.value || ""}
          onChange={(e) =>
            updateCell(row.row_id, col.column_id, e.target.value)
          }
        />
      );
    }

    if (col.column_type === "icon") {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <CustomFilesUploadButton
            btnName="Choose File"
            multiple={false}
            accept="image/*"
            disabled={!!editedCells[cellKey]?.value}
            onFilesSelected={(files) =>
              handleFilesUpload(
                files,
                row.row_id,
                col.column_id,
                "image",
              )
            }
          />

          {editedCells[cellKey]?.value && (
            <CheckCircleIcon
              color="success"
              fontSize="small"
            />
          )}
        </Box>
      );
    }

    return null;
  };

  return (
    <div>
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            padding: { xs: "8px 8px 0px 8px", md: "12px 12px 0px 12px" },
            flexShrink: 0,
          }}
        >
          <CommonBreadcrumbs
            items={[{ label: "Legal Requirements" }, { label: "Tables" }]}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            m: 2,
          }}
        >


          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: "280px" }}>
              <CustomSelectV2
                showlabelNew={false}
                label={"Search Legal Standard"}
                enableSearch={true}
                required={true}
                handleOnChange={(e) =>
                  setSelectedStandard(
                    e.target.value === "" ? null : e.target.value,
                  )
                }
                selectedValue={selectedStandard}
                optionsList={standardOptionsList || []}
                sltFldStyle={{
                  minHeight: "45px",
                  fontSize: "0.95rem",
                  borderRadius: 0,
                }}
              />
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            pl: 2,
            pr: 2,
          }}
        >
          <Box sx={{ display: "flex" }}>
            <Box sx={{ width: "280px" }}>
              <CustomSelectV2
                showlabelNew={false}
                label={"or Search Legal Table"}
                enableSearch={true}
                required={true}
                handleOnChange={(e) =>
                  setSelectedLSTable(
                    e.target.value === "" ? null : e.target.value,
                  )
                }
                selectedValue={selectedLSTable}
                optionsList={lsTableOptionsList || []}
                sltFldStyle={{
                  minHeight: "45px",
                  fontSize: "0.95rem",
                  borderRadius: 0,
                }}
              />
            </Box>
            <Box>
              <InfoButton
                outerTitle={selectedLSTableDetails?.description}
                color={"black"}
              />
            </Box>
          </Box>

          <Box sx={{ mt: 1 }}>
            <AddButtonV2 handleOnClick={() => setOpenDialogTable(true)} />
          </Box>
        </Box>
        {selectedLSTableDetails &&
          selectedLSTableDetails?.legal_table_columns?.length > 0 ? (
          <Box sx={{ mt: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Box sx={{ p: 1 }}>
                <CustomButtonV2
                  size="small"
                  variant="contained"
                  btnName="Add Row"
                  endIcon={<AddIcon />}
                  handleOnClick={handleAddRow}
                />
              </Box>
              <Box sx={{ p: 1 }}>
                <CustomButtonV2
                  size="small"
                  variant="contained"
                  btnName="Add Column"
                  endIcon={<AddIcon />}
                  handleOnClick={() => setOpenRowColumn(true)}
                />
              </Box>
            </Box>

            <TableContainer
              sx={{
                maxHeight: 250,
                maxWidth: "100%",
                overflowX: "auto",
                overflowY: "auto",
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        position: "sticky",
                        top: 0,
                        backgroundColor: "#fff",
                        zIndex: 2,
                        borderBottom: "1px solid #e0e0e0",
                      }}
                    >
                      S.No
                    </TableCell>

                    {columns.map((col) => (
                      <TableCell
                        key={col.column_id}
                        sx={{
                          position: "sticky",
                          top: 0,
                          backgroundColor: "#fff",
                          zIndex: 2,
                          borderBottom: "1px solid #e0e0e0",
                        }}
                      >
                        {col.column_label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                {console.log("rowsrowsrowsrowsrows", rows)}
                <TableBody>
                  {rows && rows.length > 0 ? (
                    rows.map((row, rowIndex) => (
                      <TableRow
                        key={row.row_id}
                        hover
                        sx={{
                          "& td": {
                            verticalAlign: "top",
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1,
                            pt: 0.5,
                            minWidth: 70,
                          }}
                        >
                          <Typography variant="body2" textAlign="end">
                            {rowIndex + 1}
                          </Typography>
                        </TableCell>

                        {columns.map((col) => {
                          const cellKey = `${row.row_id}_${col.column_id}`;

                          const submittedCell =
                            selectedLSTableDetails?.legal_table_cells?.find(
                              (cell) =>
                                cell.row_id === row.row_id &&
                                cell.column_id === col.column_id,
                            );

                          return (
                            <TableCell key={col.column_id}>
                              {renderCellContent(submittedCell, col, row, cellKey)}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length + 1}
                        align="center"
                        sx={{ py: 4 }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No table data found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{ display: "flex", justifyContent: "flex-end", mt: "10px" }}
            >
              <CustomButtonV2
                disabled={isDisableSubmit()}
                size="small"
                variant="contained"
                btnName="Submit"
                handleOnClick={handleSubmit}
                startIcon={<AddIcon />}
              />
            </Box>
          </Box>
        ) : (
          <Typography variant="body2" textAlign="center" sx={{ mt: 2 }}>
            No Table Found
          </Typography>
        )}


      </Box>

      {openDialog && (
        <ViewImageDialog
          openDialog={openDialog}
          setOpenDialog={setOpenDialog}
          imageUrl={imageUrl}
        />
      )}
      {openDialogTable && (
        <TableDialog
          fetchTotalTableDetails={fetchTotalTableDetails}
          refreshLegalStandards={refreshLegalStandards}
          selectedLSDetails={selectedStandard}
          openDialogTable={openDialogTable}
          setOpenDialogTable={setOpenDialogTable}
          selectedLSTable={selectedLSTable}
          setSelectedLSTableDetails={setSelectedLSTableDetails}
        />
      )}
      {openRowColumn && (
        <RowColumnDialog
          selectedLSTableDetails={selectedLSTableDetails}
          setSelectedLSTableDetails={setSelectedLSTableDetails}
          selectedLSDetails={selectedStandard}
          openRowColumn={openRowColumn}
          setOpenRowColumn={setOpenRowColumn}
          selectedLSTable={selectedLSTable}
        />
      )}
    </div>
  );
}

export default LegalTables;
